import { useState, useEffect } from 'react';
import type { EdhrecCard } from '@mtg-deck-tool/shared';
import { api } from '../lib/api-client.js';

export interface UseEdhrecCardsResult {
  cards: EdhrecCard[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches high-synergy or high-lift cards from EDHREC.
 * Returns up to 10 cards. On error, returns an empty array silently.
 *
 * @param cardName - The exact card name to look up EDHREC data for.
 * @param isCommander - Whether to fetch the commander panel (high-synergy)
 *   or the card panel (high-lift).
 * @returns Object with cards, isLoading, and isError state.
 */
export function useEdhrecCards(
  cardName: string,
  isCommander: boolean,
): UseEdhrecCardsResult {
  const [cards, setCards] = useState<EdhrecCard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!cardName) return;

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    api
      .getEdhrecCards(cardName, isCommander)
      .then((response) => {
        if (!cancelled) {
          setCards(response.cards);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCards([]);
          setIsError(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cardName, isCommander]);

  return { cards, isLoading, isError };
}
